"use client";
/**
 * App chrome for prompts, confirms, and alerts.
 *
 * Native `window.prompt` / `confirm` / `alert` render the host's OS dialog
 * ("app.afiatpour.com says…") and ignore theme tokens. Every user-facing
 * ask in this app goes through `useAppDialog` instead.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

const UI_FONT =
  '-apple-system, BlinkMacSystemFont, "Trebuchet MS", Roboto, Ubuntu, sans-serif';

export type AppDialogChoice = {
  value: string;
  label?: string;
};

export type AppPromptOptions = {
  title: string;
  label?: string;
  message?: string;
  initialValue?: string;
  placeholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  allowEmpty?: boolean;
  choices?: AppDialogChoice[];
};

export type AppConfirmOptions = {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
};

export type AppAlertOptions = {
  title: string;
  message: string;
  confirmLabel?: string;
};

export type AppCopyOptions = {
  title: string;
  value: string;
  message?: string;
};

export type AppDialogApi = {
  prompt: (opts: AppPromptOptions) => Promise<string | null>;
  confirm: (opts: AppConfirmOptions) => Promise<boolean>;
  alert: (opts: AppAlertOptions) => Promise<void>;
  copy: (opts: AppCopyOptions) => Promise<void>;
};

const AppDialogContext = createContext<AppDialogApi | null>(null);

type DialogState =
  | ({ kind: "prompt"; key: number } & AppPromptOptions)
  | ({ kind: "confirm"; key: number } & AppConfirmOptions)
  | ({ kind: "alert"; key: number } & AppAlertOptions)
  | ({ kind: "copy"; key: number } & AppCopyOptions);

export function useAppDialog(): AppDialogApi {
  const ctx = useContext(AppDialogContext);
  if (!ctx) {
    throw new Error("useAppDialog must be used inside AppDialogProvider");
  }
  return ctx;
}

export function AppDialogProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<DialogState | null>(null);
  const resolverRef = useRef<((value: unknown) => void) | null>(null);

  const finish = useCallback((value: unknown) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setState(null);
    resolve?.(value);
  }, []);

  const open = useCallback((next: DialogState) => {
    return new Promise<unknown>((resolve) => {
      resolverRef.current?.(next.kind === "confirm" ? false : next.kind === "prompt" ? null : undefined);
      resolverRef.current = resolve;
      setState(next);
    });
  }, []);

  const api = useMemo<AppDialogApi>(
    () => ({
      prompt: (opts) => open({ kind: "prompt", key: Date.now(), ...opts }) as Promise<string | null>,
      confirm: (opts) => open({ kind: "confirm", key: Date.now(), ...opts }) as Promise<boolean>,
      alert: async (opts) => {
        await open({ kind: "alert", key: Date.now(), ...opts });
      },
      copy: async (opts) => {
        await open({ kind: "copy", key: Date.now(), ...opts });
      },
    }),
    [open],
  );

  return (
    <AppDialogContext.Provider value={api}>
      {children}
      {state ? <AppDialogFrame key={state.key} state={state} onFinish={finish} /> : null}
    </AppDialogContext.Provider>
  );
}

function AppDialogFrame({
  state,
  onFinish,
}: {
  state: DialogState;
  onFinish: (value: unknown) => void;
}) {
  const titleId = useId();
  const fieldId = useId();
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const primaryRef = useRef<HTMLButtonElement>(null);
  const [value, setValue] = useState(state.kind === "prompt" ? (state.initialValue ?? "") : "");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const el = inputRef.current;
    if (el) {
      el.focus();
      if (state.kind === "prompt" || state.kind === "copy") el.select();
      return;
    }
    primaryRef.current?.focus();
  }, [state.kind]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        if (state.kind === "prompt") onFinish(null);
        else if (state.kind === "confirm") onFinish(false);
        else onFinish(undefined);
        return;
      }
      if (e.key === "Enter" && state.kind !== "prompt") {
        if (e.target instanceof HTMLButtonElement) return;
        e.preventDefault();
        primaryRef.current?.click();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onFinish, state.kind]);

  const cancel = () => {
    if (state.kind === "prompt") onFinish(null);
    else if (state.kind === "confirm") onFinish(false);
    else onFinish(undefined);
  };

  const submitPrompt = () => {
    if (state.kind !== "prompt") return;
    const trimmed = value.trim();
    if (!state.allowEmpty && trimmed.length === 0) return;
    onFinish(trimmed);
  };

  const copyValue = async () => {
    if (state.kind !== "copy") return;
    try {
      await navigator.clipboard.writeText(state.value);
      setCopied(true);
    } catch {
      inputRef.current?.select();
    }
  };

  const primary =
    state.kind === "confirm"
      ? state.confirmLabel ?? (state.danger ? "Delete" : "OK")
      : state.kind === "prompt"
        ? state.confirmLabel ?? "OK"
        : state.kind === "copy"
          ? copied
            ? "Copied"
            : "Copy"
          : state.confirmLabel ?? "OK";

  const canSubmit =
    state.kind !== "prompt" || Boolean(state.allowEmpty || value.trim().length > 0);

  const body = (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-ink/45 p-4 backdrop-blur-[2px]"
      style={{ fontFamily: UI_FONT }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) cancel();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-[min(24rem,94vw)] overflow-hidden rounded-xl border border-line bg-surface shadow-2xl"
      >
        <div className="flex items-center gap-3 px-4 pb-1 pt-3.5">
          <h2
            id={titleId}
            className="min-w-0 flex-1 truncate text-[15px] font-semibold tracking-tight text-ink"
          >
            {state.title}
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={cancel}
            className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-ink-muted transition hover:bg-surface-muted hover:text-ink"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-4 pb-3 pt-2">
          {"message" in state && state.message ? (
            <p className="mb-3 whitespace-pre-wrap text-[13px] leading-relaxed text-ink-muted">
              {state.message}
            </p>
          ) : null}

          {state.kind === "prompt" && (
            <>
              {state.label ? (
                <label htmlFor={fieldId} className="mb-1.5 block text-[11px] font-medium text-ink-muted">
                  {state.label}
                </label>
              ) : null}
              <input
                ref={inputRef}
                id={fieldId}
                type="text"
                value={value}
                placeholder={state.placeholder}
                list={state.choices?.length ? listId : undefined}
                autoComplete="off"
                spellCheck={false}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    submitPrompt();
                  }
                }}
                className="w-full rounded-md border border-line bg-surface px-2.5 py-2 text-[13px] text-ink outline-none transition placeholder:text-ink-faint focus:border-accent focus:ring-2 focus:ring-accent/25"
              />
              {state.choices && state.choices.length > 0 ? (
                <datalist id={listId}>
                  {state.choices.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label ?? c.value}
                    </option>
                  ))}
                </datalist>
              ) : null}
            </>
          )}

          {state.kind === "copy" && (
            <input
              ref={inputRef}
              type="text"
              readOnly
              value={state.value}
              onFocus={(e) => e.currentTarget.select()}
              className="w-full rounded-md border border-line bg-surface-sunken px-2.5 py-2 text-[12px] text-ink outline-none"
            />
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-line bg-surface-sunken/40 px-4 py-3">
          {state.kind !== "alert" && (
            <button
              type="button"
              onClick={cancel}
              className="rounded-md border border-line bg-surface px-3 py-1.5 text-[12px] font-medium text-ink transition hover:bg-surface-muted"
            >
              {state.kind === "copy" ? "Close" : (state.kind === "prompt" || state.kind === "confirm" ? state.cancelLabel : undefined) ?? "Cancel"}
            </button>
          )}
          <button
            ref={primaryRef}
            type="button"
            disabled={state.kind === "prompt" && !canSubmit}
            onClick={() => {
              if (state.kind === "prompt") submitPrompt();
              else if (state.kind === "confirm") onFinish(true);
              else if (state.kind === "copy") void copyValue();
              else onFinish(undefined);
            }}
            className={
              state.kind === "confirm" && state.danger
                ? "rounded-md bg-bear px-3 py-1.5 text-[12px] font-semibold text-on-bear transition hover:opacity-90"
                : "rounded-md bg-accent px-3 py-1.5 text-[12px] font-semibold text-on-accent transition hover:opacity-90 disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-ink-faint disabled:opacity-100"
            }
          >
            {primary}
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") return null;
  return createPortal(body, document.body);
}
